<script lang="ts">
    import dayjs from 'dayjs';
    import { createDatePicker } from '@melt-ui/svelte';
    import { parseDate } from '@internationalized/date';
    import { VpPlayIcon } from '@decathlon-experience/icons/svelte';
    import { VpChevronRightIcon } from '@decathlon-experience/icons/svelte';

    import {
        findFirstAvailableMonth,
        formatTourDate,
        formatTourDate2,
        isDateDisabled as isDateDisabledUtil,
    } from '$saga/utils/date';
    import cn from '$common/utils/cn';
    import { TOUR_TYPE_FIT, TOUR_TYPE_GIR } from '$saga/constants/tour';
    import Button from '$saga/components/Button.svelte';
    import CalendarPicker from '$saga/components/CalendarPicker.svelte';
    import {
        datesInfos,
        dateValue,
        selectedPrice,
    } from '$saga/views/Tour/store';
    import {
        getCurrentDayPriceAmount,
        getQuantileSizeClass,
    } from '$saga/views/Tour/VisibleDates/utils';
    import type {
        MinPriceDays,
        VisibleDate,
    } from '$saga/views/Tour/VisibleDates/types';
    import type { ClosedSales } from '$saga/views/Tour/types';
    import { formatCurrency } from '$saga/utils/price';

    export let dates: VisibleDate[],
        tourType: number,
        handleNextStep: (date: string) => void,
        closedSales: ClosedSales,
        minPricePerDays: MinPriceDays,
        overflowContainer: HTMLElement | null;

    const NUMBER_OF_MONTHS = 3;

    const {
        elements: { calendar, cell, grid, heading, nextButton, prevButton },
        states: { value, months, headingValue, weekdays },
        helpers: { isDateDisabled, isDateUnavailable, setMonth, setYear },
    } = createDatePicker({
        locale: 'fr',
        name: 'selectedDate',
        numberOfMonths: NUMBER_OF_MONTHS,
        pagedNavigation: true,
        minValue: parseDate(dayjs().add(1, 'day').format('YYYY-MM-DD')),
        isDateDisabled: (date) =>
            isDateDisabledUtil(date, closedSales, minPricePerDays),
        value: dateValue,
    });

    $: {
        // find the closest date available, if not in current month then set
        // to the closest date's month
        // dayjs starts at 0, melt-ui at 1
        // + one more offset to display the 1st available month on the left
        const firstAvailableDate = findFirstAvailableMonth(closedSales);
        setYear(firstAvailableDate.year());
        setMonth(firstAvailableDate.month() + 1);
    }

    const handleGirDateSelection = (selectedItem: VisibleDate) => {
        if (selectedItem?.date1?.date != null) {
            $selectedPrice = {
                originalPrice: selectedItem.price,
                actualPrice: selectedItem.hasRebate
                    ? selectedItem.rebatedPrice
                    : selectedItem.price,
                rebateAmount: selectedItem.rebateAmount,
            };
            const date = dayjs(selectedItem.date1.date).format('YYYY-MM-DD');
            dateValue.set(parseDate(date));
            handleNextStep(dayjs(selectedItem.date1.date).format('YYYY-MM-DD'));
            $datesInfos = {
                price: selectedItem.hasRebate
                    ? selectedItem.rebatedPrice
                    : selectedItem.price,
                remainingStock: selectedItem?.remaining_stock ?? '',
                statusName: selectedItem?.statusName ?? '',
                selectedDate: date,
            };
        }
    };

    const handleFitDateSelection = () => {
        const date = dayjs($dateValue?.toString()).format('YYYY-MM-DD');
        const selected = minPricePerDays[date];
        if (selected) {
            $selectedPrice = {
                originalPrice: selected.originalAmount
                    ? selected.originalAmount / 100
                    : null,
                rebateAmount: selected.rebateAmount
                    ? `-${selected.rebateAmount / 100}€`
                    : null,
                actualPrice: selected.amount / 100,
            };
            $dateValue = parseDate(date);
            $datesInfos = {
                price: null,
                remainingStock: null,
                statusName: '',
                selectedDate: date,
            };
            handleNextStep(date);
        }
    };
</script>

{#if tourType === TOUR_TYPE_GIR}
    <div class="py-l">
        <h3 class="my-s text-center font-semibold">
            Sélectionnez une date de départ
        </h3>
        {#each dates as date (date.id)}
            <button
                type="button"
                class="flex w-full border-b border-solid border-border-quiet py-xs text-sm"
                disabled={date.isFull}
                on:click={() => !date.isFull && handleGirDateSelection(date)}
            >
                <span class="flex w-full flex-col gap-1 text-left">
                    <span class={cn(date.isFull && 'opacity-[0.38]')}>
                        du <span class="font-semibold"
                            >{formatTourDate(date.date1.date)}</span
                        >
                        au
                        <span class="font-semibold"
                            >{formatTourDate2(date.date2.date)}</span
                        >
                    </span>
                    {#if date.isFull}
                        <span class="font-semibold text-content-brand">
                            Complet
                        </span>
                    {:else}
                        <span class="text-content-brand">
                            <span class="font-semibold">{date.statusName}</span>
                        </span>
                    {/if}
                    {#if date.remaining_stock > 0}
                        <span class="text-xs">
                            {date.remaining_stock} places disponibles
                            {#if date.paxSoldCount > 0}
                                <span>- {date.paxSoldCount} inscrits</span>
                            {/if}
                        </span>
                    {/if}
                    <span
                        class={cn(
                            'flex items-center gap-xs',
                            date.isFull && 'opacity-[0.38]',
                        )}
                    >
                        {#if date.hasRebate}
                            <span class="line-through">
                                {formatCurrency(date.price, true)}
                            </span>
                            <span
                                class="w-fit bg-container-commercial-catchy p-2xs text-xs"
                            >
                                {date.rebateAmount}
                            </span>

                            {#if date.rebatedPrice}
                                <span class="pl-xs font-semibold">
                                    {formatCurrency(date.rebatedPrice, true)}
                                </span>
                            {/if}
                        {:else}
                            <span class="font-semibold">
                                {formatCurrency(date.price, true)}
                            </span>
                        {/if}
                    </span>
                </span>
                <span class="m-auto h-full p-m">
                    <VpChevronRightIcon
                        class={cn(
                            'size-xl text-content-neutral',
                            date.isFull && 'text-content-quiet opacity-[0.38]',
                        )}
                    />
                </span>
            </button>
        {/each}
    </div>
{:else if tourType === TOUR_TYPE_FIT}
    <div class="flex flex-col gap-xl">
        <h3 class="pt-m text-center font-semibold">
            Sélectionnez une date de départ
        </h3>
        <CalendarPicker
            {calendar}
            {prevButton}
            {nextButton}
            {heading}
            {headingValue}
            {months}
            {grid}
            {weekdays}
            {isDateDisabled}
            {isDateUnavailable}
            {cell}
            numberOfMonths={NUMBER_OF_MONTHS}
            monthsOrientation="vertical"
            getDayAdditionalStyle={(date) =>
                getQuantileSizeClass(
                    minPricePerDays[dayjs(date.toString()).format('YYYY-MM-DD')]
                        ?.quantile,
                )}
            getDayAdditionalContent={(date) =>
                getCurrentDayPriceAmount(minPricePerDays, date)}
            {overflowContainer}
        />
        <!-- Footer -->
        <div
            class="sticky bottom-0 left-0 flex w-full justify-between border-t border-solid border-border-quiet bg-white py-s"
        >
            <Button
                intent={$value ? 'primary' : 'disabled'}
                class="w-full"
                disabled={$value == null}
                on:click={handleFitDateSelection}
                endIcon={VpPlayIcon}
            >
                Continuer
            </Button>
        </div>
    </div>
{/if}
